import { BranchStep } from 'dorian-shared/types/branch/BranchStep';
import {
  HubOption, HubOptionStateValue, StateCondition, isContinueOption,
} from 'dorian-shared/types/branch/HubOption';
import type { ReplayCondition, HubProgressBar } from 'dorian-shared/types/branch/HubOption';
import { isValidBorderHex } from './borderConditionValidation';
import {
  HUB_HEADER_MAX_LENGTH,
  HUB_SUBHEADER_MAX_LENGTH,
  HUB_CARD_TEXT_MAX_LENGTH,
  HUB_CONTINUE_MAX_LENGTH,
  HUB_PROGRESS_BAR_LABEL_MAX_LENGTH,
} from './hubStepDefaults';

export type HubValidationResult = {
  valid: boolean;
  errors: string[];
  warnings: string[];
};

type OptionValidatorResult = { errors: string[]; warnings: string[] };
type OptionValidator = (option: HubOption, optionIndex: number) => OptionValidatorResult;

/**
 * Per-option validators. Feature branches register their validators here.
 */
const optionValidators: OptionValidator[] = [];

/** Register an option-level validator. Called once at module load by feature modules. */
export function registerOptionValidator(validator: OptionValidator): void {
  optionValidators.push(validator);
}

/**
 * Returns true when an option has any default state, condition, replay default,
 * or replay condition that can place it in `hidden` or `disabled` state.
 *
 * Exhaustive: only state-related sources affect availability. Other overrides
 * (text, destination, icon, tags, border) change content, not visibility.
 */
export function canOptionBeHiddenOrDisabled(option: HubOption): boolean {
  if (option.state.state !== HubOptionStateValue.Active) {
    return true;
  }

  const stateConditions = option.stateConditions ?? [];
  if (stateConditions.some(
    (c) => c.resultState === HubOptionStateValue.Hidden
        || c.resultState === HubOptionStateValue.Disabled,
  )) {
    return true;
  }

  if (option.replay && option.replay.state !== HubOptionStateValue.Active) {
    return true;
  }

  const replayConditions = option.replayConditions ?? [];
  if (replayConditions.some(
    (c) => c.resultState === HubOptionStateValue.Hidden
        || c.resultState === HubOptionStateValue.Disabled,
  )) {
    return true;
  }

  return false;
}

/**
 * Returns true when at least one option (regular or Continue) is guaranteed to
 * stay visible and active — no default state, replay default, or condition can
 * push it into hidden/disabled.
 *
 * Use this to surface a step-level indicator without blocking Save. Backend
 * mirrors this check at publish time (see `atLeastOneAlwaysAvailableOption`
 * rule in editor-api).
 */
export function hasAtLeastOneAlwaysAvailableOption(options: HubOption[]): boolean {
  if (options.length === 0) return false;
  return options.some((option) => !canOptionBeHiddenOrDisabled(option));
}

/**
 * Validate sub-conditions shared by state and replay conditions.
 * Returns error strings for invalid sub-conditions.
 */
function validateSubConditions(
  subConditions: { variableId: number | null; intValue?: string; varValue?: number }[],
  prefix: string,
): string[] {
  const errors: string[] = [];

  if (subConditions.length === 0) {
    errors.push(`${prefix}: must have at least 1 sub-condition.`);
  }

  subConditions.forEach((sub, subIdx) => {
    if (sub.variableId === null || sub.variableId === undefined) {
      errors.push(`${prefix}, sub-condition #${subIdx + 1}: memory variable is required.`);
      return;
    }

    const hasIntValue = sub.intValue !== undefined && sub.intValue !== '';
    const hasVarValue = sub.varValue !== undefined && sub.varValue !== null && sub.varValue !== 0;
    if (!hasIntValue && !hasVarValue) {
      errors.push(`${prefix}, sub-condition #${subIdx + 1}: a comparison value is required.`);
    }
    if (hasIntValue && hasVarValue) {
      errors.push(`${prefix}, sub-condition #${subIdx + 1}: both literal and variable values are set (data integrity violation).`);
    }
  });

  return errors;
}

function validateStateCondition(
  condition: StateCondition,
  optionIndex: number,
  conditionIndex: number,
): string[] {
  const prefix = `Option #${optionIndex + 1}, state condition #${conditionIndex + 1}`;
  return validateSubConditions(condition.subConditions, prefix);
}

/**
 * Validate state conditions for a single option.
 * Returns errors and warnings.
 */
function validateOptionStateConditions(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const conditions = option.stateConditions ?? [];

  conditions.forEach((cond, condIdx) => {
    errors.push(...validateStateCondition(cond, optionIndex, condIdx));
  });

  // Warning: missing disabledText when resultState is 'disabled'
  conditions.forEach((cond, condIdx) => {
    if (cond.resultState === 'disabled' && !cond.disabledText?.trim()) {
      warnings.push(
        `Option #${optionIndex + 1}, state condition #${condIdx + 1}: "Disabled" state has no explanation text — player will see a disabled option without context.`,
      );
    }
  });

  // Warning: duplicate resultState values (redundant but not broken)
  const resultStates = conditions.map((c) => c.resultState);
  const seen = new Set<string>();
  resultStates.forEach((state) => {
    if (seen.has(state)) {
      return; // already warned for this state value
    }
    const count = resultStates.filter((s) => s === state).length;
    if (count > 1) {
      warnings.push(
        `Option #${optionIndex + 1}: multiple state conditions with result "${state}" — only the first match will apply.`,
      );
    }
    seen.add(state);
  });

  return { errors, warnings };
}

function validateReplayCondition(
  condition: ReplayCondition,
  optionIndex: number,
  conditionIndex: number,
): string[] {
  const prefix = `Option #${optionIndex + 1}, replay condition #${conditionIndex + 1}`;
  const errors = validateSubConditions(condition.subConditions, prefix);

  if (condition.resultState == null) {
    errors.push(`${prefix}: state is required.`);
  } else if (condition.resultState === 'active' && condition.resultGotoBranchId == null) {
    errors.push(`${prefix}: destination is required when state is Active.`);
  }

  return errors;
}

function validateOptionReplayConfig(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  const defaultActive = option.replay && option.replay.state !== HubOptionStateValue.Hidden;
  if (defaultActive && option.replay!.gotoBranchId == null) {
    errors.push(`Option #${optionIndex + 1}: replay is active but no default destination is set.`);
  }

  const conditions = option.replayConditions ?? [];

  conditions.forEach((cond, condIdx) => {
    errors.push(...validateReplayCondition(cond, optionIndex, condIdx));
  });

  conditions.forEach((cond, condIdx) => {
    if (cond.resultGotoBranchId == null && cond.resultState === HubOptionStateValue.Active) {
      warnings.push(
        `Option #${optionIndex + 1}, replay condition #${condIdx + 1}: no destination override — will use default replay destination.`,
      );
    }
  });

  for (let i = 0; i < conditions.length; i++) {
    for (let j = i + 1; j < conditions.length; j++) {
      const a = conditions[i];
      const b = conditions[j];
      if (
        a.quantifier === b.quantifier
        && a.subConditions.length === b.subConditions.length
        && a.subConditions.every((sub, idx) => {
          const other = b.subConditions[idx];
          return (
            sub.variableId === other?.variableId
            && sub.operator === other?.operator
            && sub.intValue === other?.intValue
            && sub.varValue === other?.varValue
          );
        })
      ) {
        warnings.push(
          `Option #${optionIndex + 1}: replay conditions #${i + 1} and #${j + 1} have identical triggers — only the first match will apply.`,
        );
      }
    }
  }

  return { errors, warnings };
}

function validateTextDestCondition(
  condition: { subConditions: { variableId: number | null; intValue?: string; varValue?: number }[] },
  optionIndex: number,
  conditionIndex: number,
): string[] {
  const prefix = `Option #${optionIndex + 1}, text & dest condition #${conditionIndex + 1}`;
  return validateSubConditions(condition.subConditions, prefix);
}

function validateOptionTextDestConditions(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const conditions = option.textDestConditions ?? [];

  conditions.forEach((cond, condIdx) => {
    errors.push(...validateTextDestCondition(cond, optionIndex, condIdx));

    if (cond.resultGotoBranchId == null) {
      errors.push(
        `Option #${optionIndex + 1}, text & dest condition #${condIdx + 1}: destination is required.`,
      );
    }
  });

  return { errors, warnings };
}

function validateOptionIconConditions(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const conditions = option.iconConditions ?? [];

  conditions.forEach((cond, condIdx) => {
    const prefix = `Option #${optionIndex + 1}, icon condition #${condIdx + 1}`;
    errors.push(...validateSubConditions(cond.subConditions, prefix));

    if (cond.resultIconId == null) {
      errors.push(`${prefix}: an icon must be selected.`);
    }
  });

  return { errors, warnings };
}

function validateOptionTagConditions(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  const topConditions = option.tagTopConditions ?? [];
  topConditions.forEach((cond, condIdx) => {
    const prefix = `Option #${optionIndex + 1}, top tag condition #${condIdx + 1}`;
    errors.push(...validateSubConditions(cond.subConditions, prefix));
  });

  const bottomConditions = option.tagBottomConditions ?? [];
  bottomConditions.forEach((cond, condIdx) => {
    const prefix = `Option #${optionIndex + 1}, bottom tag condition #${condIdx + 1}`;
    errors.push(...validateSubConditions(cond.subConditions, prefix));
  });

  return { errors, warnings };
}

function validateOptionBorderConditions(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (option.borderColor != null && option.borderColor !== '' && !isValidBorderHex(option.borderColor)) {
    errors.push(`Option #${optionIndex + 1}: default border color must be a valid hex (#rrggbb or #rrggbbaa).`);
  }

  const conditions = option.borderConditions ?? [];
  conditions.forEach((cond, condIdx) => {
    const prefix = `Option #${optionIndex + 1}, border condition #${condIdx + 1}`;
    errors.push(...validateSubConditions(cond.subConditions, prefix));
    if (!isValidBorderHex(cond.color)) {
      errors.push(`${prefix}: a valid hex color is required.`);
    }
  });

  return { errors, warnings };
}

function validateOptionScore(
  option: HubOption,
  optionIndex: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const { score } = option;
  if (!score) return { errors, warnings };

  const prefix = `Option #${optionIndex + 1}, score`;

  const hasNumerator = score.numeratorType === 'literal'
    ? score.numeratorLiteral != null && score.numeratorLiteral !== ''
    : score.numeratorVariableId != null;
  const hasDenominator = score.denominatorType === 'literal'
    ? score.denominatorLiteral != null && score.denominatorLiteral !== ''
    : score.denominatorVariableId != null;

  // Fully empty score is valid (intentionally unset).
  if (!hasNumerator && !hasDenominator) return { errors, warnings };

  // Partial fill: one side set, other empty — broken configuration.
  if (hasNumerator !== hasDenominator) {
    errors.push(`${prefix}: ${hasNumerator ? 'denominator' : 'numerator'} is required.`);
    return { errors, warnings };
  }

  // Both literal: only meaningful when numerator < denominator (proper fraction).
  if (score.numeratorType === 'literal' && score.denominatorType === 'literal') {
    const num = Number(score.numeratorLiteral);
    const den = Number(score.denominatorLiteral);
    if (Number.isFinite(num) && Number.isFinite(den)) {
      if (den === 0) {
        errors.push(`${prefix}: denominator cannot be zero.`);
      }
    }
  }

  // Mutual exclusion of variable refs is enforced by the picker UI (excludeVariableId)
  // — no validator needed here.

  return { errors, warnings };
}

// Register validators
registerOptionValidator(validateOptionStateConditions);
registerOptionValidator(validateOptionReplayConfig);
registerOptionValidator(validateOptionTextDestConditions);
registerOptionValidator(validateOptionIconConditions);
registerOptionValidator(validateOptionTagConditions);
registerOptionValidator(validateOptionBorderConditions);
registerOptionValidator(validateOptionScore);

function validateProgressBarSide(
  side: 'numerator' | 'denominator',
  type: 'literal' | 'variable',
  variableId: number | null,
  literal: string | null,
  prefix: string,
): string[] {
  if (type === 'variable' && variableId == null) {
    return [`${prefix}: ${side} variable is required.`];
  }
  if (type === 'literal' && (literal == null || literal === '')) {
    return [`${prefix}: ${side} value is required.`];
  }
  return [];
}

// Mirrors backend hub_progress_bar.js#_validateSide — surface errors before the API 400s.
export function validateProgressBar(
  bar: HubProgressBar,
  idx: number,
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const prefix = `Progress bar #${idx + 1}`;

  if (!bar.label?.trim()) {
    errors.push(`${prefix}: label is required.`);
  } else if (bar.label.length > HUB_PROGRESS_BAR_LABEL_MAX_LENGTH) {
    warnings.push(`${prefix}: label exceeds ${HUB_PROGRESS_BAR_LABEL_MAX_LENGTH} characters.`);
  }

  errors.push(
    ...validateProgressBarSide('numerator', bar.numeratorType, bar.numeratorVariableId, bar.numeratorLiteral, prefix),
  );
  errors.push(
    ...validateProgressBarSide('denominator', bar.denominatorType, bar.denominatorVariableId, bar.denominatorLiteral, prefix),
  );

  // Catches data from clone/paste/direct API; the dropdown already excludes this.
  if (
    bar.numeratorType === 'variable'
    && bar.denominatorType === 'variable'
    && bar.numeratorVariableId != null
    && bar.numeratorVariableId === bar.denominatorVariableId
  ) {
    errors.push(`${prefix}: numerator and denominator reference the same variable.`);
  }

  return { errors, warnings };
}

export function validateHubStep(step: BranchStep): HubValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const hubOptions = step.hubOptions ?? [];
  const regularOptions = hubOptions.filter((o) => !isContinueOption(o));

  // Error: at least 1 non-Continue option
  if (regularOptions.length === 0) {
    errors.push('Hub step requires at least one option (besides Continue).');
  }

  // Warning: empty titles
  const emptyTitles = regularOptions.filter((o) => !o.title?.trim());
  if (emptyTitles.length > 0) {
    warnings.push(`${emptyTitles.length} option(s) have empty titles.`);
  }

  // Warning: missing destinations
  const missingDest = regularOptions.filter((o) => !o.gotoBranchId);
  if (missingDest.length > 0) {
    warnings.push(`${missingDest.length} option(s) have no destination set.`);
  }

  // Warning: header text exceeds limit
  const headerLen = step.hubSettings?.header?.text?.length ?? 0;
  if (headerLen > HUB_HEADER_MAX_LENGTH) {
    warnings.push(`Header text exceeds ${HUB_HEADER_MAX_LENGTH} characters (${headerLen}).`);
  }

  // Warning: subheader text exceeds limit
  const subheaderLen = step.hubSettings?.subheader?.text?.length ?? 0;
  if (subheaderLen > HUB_SUBHEADER_MAX_LENGTH) {
    warnings.push(`Subheader text exceeds ${HUB_SUBHEADER_MAX_LENGTH} characters (${subheaderLen}).`);
  }

  // Warning: regular option titles exceed limit
  const longRegularTitles = regularOptions.filter((o) => (o.title?.length ?? 0) > HUB_CARD_TEXT_MAX_LENGTH);
  if (longRegularTitles.length > 0) {
    warnings.push(`${longRegularTitles.length} option(s) have titles exceeding ${HUB_CARD_TEXT_MAX_LENGTH} characters.`);
  }

  // Warning: continue option title exceeds limit
  const continueOption = hubOptions.find((o) => isContinueOption(o));
  if (continueOption && (continueOption.title?.length ?? 0) > HUB_CONTINUE_MAX_LENGTH) {
    warnings.push(`Continue button text exceeds ${HUB_CONTINUE_MAX_LENGTH} characters (${continueOption.title?.length}).`);
  }

  // Run per-option validators (state conditions, replay conditions, etc.)
  regularOptions.forEach((option, idx) => {
    optionValidators.forEach((validate) => {
      const result = validate(option, idx);
      errors.push(...result.errors);
      warnings.push(...result.warnings);
    });
  });

  // V1.6: registerOptionValidator covers regularOptions only; Continue is validated
  // explicitly here. Do NOT also register validateOptionScore for Continue — would duplicate.
  if (continueOption) {
    const scoreResult = validateOptionScore(continueOption, hubOptions.indexOf(continueOption));
    errors.push(...scoreResult.errors);
    warnings.push(...scoreResult.warnings);

    const borderResult = validateOptionBorderConditions(continueOption, hubOptions.indexOf(continueOption));
    errors.push(...borderResult.errors);
    warnings.push(...borderResult.warnings);
  }

  // V1.5: per-progress-bar validation
  const progressBars = step.hubSettings?.progressBars ?? [];
  progressBars.forEach((bar, idx) => {
    const result = validateProgressBar(bar, idx);
    errors.push(...result.errors);
    warnings.push(...result.warnings);
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
